import { sectionRenderer } from '@theme/section-renderer';

/**
 * <ax-nosto-grid-fallback> — chien de garde du repli des grilles rendues par Nosto
 * (page collection et page de recherche, qui partagent la même mécanique).
 *
 * Le problème qu'il résout : la grille de ces pages est entièrement rendue
 * par le bundle Nosto, qui pose la classe `nosto-loaded` sur `<nosto-react>` une fois
 * les premières cartes prêtes (cf. `_nosto/src/components/ContentWrapper`). Le CSS par
 * défaut cache `#nosto-content` et montre le skeleton ; SEULE cette classe bascule.
 * Si le bundle ne charge pas — CDN Nosto injoignable, script bloqué, erreur au
 * démarrage — rien ne bascule et la page reste en shimmer indéfiniment, sans un seul
 * produit. Il n'existait aucun repli : la classe `nosto-fallback` que le CSS prévoyait
 * n'était posée par aucun code.
 *
 * Ce composant referme ce trou : passé le délai, il redemande la section au serveur en
 * mode secours (`?nostoFallback=1`) et laisse le morph remplacer toute la section par
 * la grille native. La réponse de secours ne contient ni `nosto-react` ni ce chien de
 * garde — le remplacement est donc terminal, il ne peut pas boucler. Vérifié : un
 * bundle Nosto qui arrive APRÈS le repli ne lève aucune erreur et ne reprend pas la
 * main, faute de point de montage.
 *
 * ── Deux échéances, et non une ───────────────────────────────────────────────────────
 * Un délai unique serait un mauvais arbitrage : en 4G bridée, Nosto met déjà 6,4 à 6,9 s
 * à afficher ses produits (mesuré). Couper à 8 s reviendrait à remplacer régulièrement
 * des résultats PERSONNALISÉS par la grille native chez les visiteurs sur connexion
 * lente — on réparerait une panne rare en cassant le cas courant.
 *
 * D'où deux échéances, sur un critère de « signe de vie » :
 *   • à `data-timeout` (8 s) — on ne bascule QUE si Nosto n'a donné aucun signe de vie
 *     (ni `window.nostojs`, ni rien de monté dans `#nosto-content`). C'est le cas
 *     « Nosto absent » : script bloqué, CDN injoignable, erreur au démarrage.
 *   • à `data-hard-timeout` (20 s) — on bascule quoi qu'il arrive. C'est le cas
 *     « Nosto monté mais bloqué » (API de recherche muette) : le shimmer ne partira
 *     jamais tout seul, il faut bien trancher.
 *
 * Les deux échéances comptent depuis le DÉBUT DE LA NAVIGATION, pas depuis le montage de
 * ce composant : le module qui le définit charge lui-même à un instant variable, une
 * échéance relative rendrait le comportement imprévisible.
 */

const DEFAULT_TIMEOUT_MS = 8000;
const DEFAULT_HARD_TIMEOUT_MS = 20000;
const LOADED_CLASS = 'nosto-loaded';

class AxCollectionNostoFallback extends HTMLElement {
  /** @type {number | null} */
  #timer = null;
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    const root = document.querySelector('nosto-react');

    // Pas de racine Nosto (branche Nosto OFF, ou section déjà remplacée) : rien à surveiller.
    if (!root) return;
    if (root.classList.contains(LOADED_CLASS)) return;

    this.#observer = new MutationObserver(() => {
      if (root.classList.contains(LOADED_CLASS)) this.#standDown();
    });
    this.#observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    this.#armAt(this.#timeout, false);
  }

  disconnectedCallback() {
    this.#standDown();
  }

  /** @returns {number} Échéance douce, depuis le début de la navigation. */
  get #timeout() {
    return parseInt(this.dataset.timeout ?? '', 10) || DEFAULT_TIMEOUT_MS;
  }

  /** @returns {number} Échéance ferme, depuis le début de la navigation. */
  get #hardTimeout() {
    return parseInt(this.dataset.hardTimeout ?? '', 10) || DEFAULT_HARD_TIMEOUT_MS;
  }

  /**
   * Programme un réveil à une échéance absolue (comptée depuis le début de la
   * navigation), pas à un délai relatif au montage de ce composant.
   * @param {number} deadline
   * @param {boolean} isHard - true = on bascule quoi qu'il arrive.
   */
  #armAt(deadline, isHard) {
    const remaining = Math.max(0, deadline - performance.now());
    this.#timer = window.setTimeout(() => this.#check(isHard), remaining);
  }

  /**
   * Nosto a-t-il donné signe de vie ? On ne veut pas préempter un Nosto simplement
   * lent : tant qu'il progresse, on le laisse finir.
   * @returns {boolean}
   */
  #nostoIsAlive() {
    if (typeof window.nostojs === 'function') return true;

    const content = document.getElementById('nosto-content');
    return !!content && content.childElementCount > 0;
  }

  /** @param {boolean} isHard */
  #check(isHard) {
    this.#timer = null;

    if (document.querySelector('nosto-react')?.classList.contains(LOADED_CLASS)) {
      this.#standDown();
      return;
    }

    // Échéance douce et Nosto donne signe de vie : il est lent, pas absent. On lui
    // laisse jusqu'à l'échéance ferme.
    if (!isHard && this.#nostoIsAlive()) {
      this.#armAt(this.#hardTimeout, true);
      return;
    }

    this.#renderFallback();
  }

  /** Nosto a monté (ou on quitte la page) : on désarme tout. */
  #standDown() {
    if (this.#timer != null) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }

    this.#observer?.disconnect();
    this.#observer = null;
  }

  async #renderFallback() {
    this.#standDown();

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('nostoFallback', '1');

    try {
      // `cache: false` : cette réponse ne doit jamais servir un autre rendu de la section.
      await sectionRenderer.renderSection(sectionId, { url, cache: false });
    } catch (error) {
      // Le repli du repli, c'est le skeleton. On ne casse rien de plus.
      console.error('[ax-nosto-grid-fallback] rendu de secours impossible', error);
    }
  }
}

if (!customElements.get('ax-nosto-grid-fallback')) {
  customElements.define('ax-nosto-grid-fallback', AxCollectionNostoFallback);
}
